import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from 'recharts';
import { api } from './auth';

const VERDICT_COLORS = {
  COMPLIANT: '#16A34A',
  'NON-COMPLIANT': '#DC2626',
  INCOMPLETE: '#D97706',
  ERROR: '#6B7280',
};

export default function Dashboard({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [filterCompanyId, setFilterCompanyId] = useState('');

  useEffect(() => {
    if (user?.role === 'super_admin') {
      api.get('/admin/companies').then(r => setCompanies(r.data || [])).catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const q = filterCompanyId ? `?company_id=${filterCompanyId}` : '';
    api.get(`/admin/dashboard${q}`)
      .then(r => setData(r.data))
      .catch(err => {
        console.error('Dashboard load error:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to load dashboard');
      })
      .finally(() => setLoading(false));
  }, [filterCompanyId]);

  if (loading) {
    return <div style={card}><p style={{ color: '#9CA3AF' }}>Loading dashboard…</p></div>;
  }

  if (error) {
    return <div style={card}>
      <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 8px 0' }}>Dashboard</h2>
      <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 16px', color: '#DC2626', fontSize: 13 }}>
        <strong>Couldn't load dashboard.</strong> {error}
        <br /><br />
        <span style={{ fontSize: 12, color: '#991B1B' }}>Check your browser console for details. The backend may need to be redeployed.</span>
      </div>
    </div>;
  }

  if (!data) {
    return <div style={card}><p style={{ color: '#9CA3AF' }}>No data available.</p></div>;
  }

  // Defensive defaults — handle missing or partial data gracefully
  const stats = data.stats || { today: 0, week: 0, month: 0, total: 0 };
  const verdicts = data.verdicts || {};
  const dailyVolume = data.daily_volume || [];
  const topUsers = data.top_users || [];
  const recentActivity = data.recent_activity || [];

  const verdictData = Object.entries(verdicts).map(([k, v]) => ({ name: k, value: v }));
  const isAdmin = user?.role !== 'verifier';
  const isSuper = user?.role === 'super_admin';
  const hasAnyData = dailyVolume.some(d => (d.voice || 0) + (d.pdf || 0) > 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Dashboard</h2>
        {isSuper && companies.length > 0 && (
          <select value={filterCompanyId} onChange={e => setFilterCompanyId(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 12, fontFamily: 'inherit' }}>
            <option value="">All companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 12, marginBottom: 16 }}>
        <Stat label="Today" value={stats.today || 0} />
        <Stat label="Last 7 days" value={stats.week || 0} />
        <Stat label="This month" value={stats.month || 0} />
        <Stat label="All time" value={stats.total || 0} accent />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>
        <div style={card}>
          <p style={cardTitle}>Verdict breakdown</p>
          {verdictData.length === 0 ? <p style={empty}>No verifications yet</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={verdictData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {verdictData.map((entry, i) => <Cell key={i} fill={VERDICT_COLORS[entry.name] || '#9CA3AF'} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #E5E7EB', borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div style={card}>
          <p style={cardTitle}>Daily volume — last 30 days</p>
          {!hasAnyData ? <p style={empty}>No verifications yet</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={dailyVolume} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={Math.ceil(dailyVolume.length / 6)} tickFormatter={d => (d || '').slice(5)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #E5E7EB', borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="voice" stroke="#2563EB" strokeWidth={2} dot={false} name="Voice" />
                <Line type="monotone" dataKey="pdf" stroke="#7C3AED" strokeWidth={2} dot={false} name="PDF" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? 'repeat(auto-fit, minmax(320px, 1fr))' : '1fr', gap: 16 }}>
        {isAdmin && (
          <div style={card}>
            <p style={cardTitle}>Top users</p>
            {topUsers.length === 0 ? <p style={empty}>No activity yet</p> : (
              <table style={tbl}>
                <thead><tr><th style={th}>User</th><th style={{ ...th, textAlign: 'right' }}>Verifications</th></tr></thead>
                <tbody>
                  {topUsers.map((u, i) => (
                    <tr key={i} style={{ borderBottom: '0.5px solid #F3F4F6' }}>
                      <td style={td}>{u.email || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{u.count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div style={card}>
          <p style={cardTitle}>Recent activity</p>
          {recentActivity.length === 0 ? <p style={empty}>No recent activity</p> : (
            <div>
              {recentActivity.map(v => (
                <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid #F3F4F6' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.filename || 'Unknown'}</p>
                    <p style={{ fontSize: 11, color: '#9CA3AF', margin: '2px 0 0 0' }}>
                      {(v.source || '').toUpperCase()} · {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                      {isSuper && v.company_name && ` · ${v.company_name}`}
                      {isAdmin && v.user_email && ` · ${v.user_email}`}
                    </p>
                  </div>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: `${VERDICT_COLORS[v.verdict] || '#6B7280'}15`, color: VERDICT_COLORS[v.verdict] || '#6B7280', fontWeight: 600, marginLeft: 8 }}>{v.verdict || '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return <div style={{ background: accent ? '#111827' : '#fff', color: accent ? '#fff' : '#111827', borderRadius: 10, padding: '16px 18px', border: accent ? 'none' : '1px solid #E5E7EB' }}>
    <p style={{ fontSize: 11, color: accent ? 'rgba(255,255,255,0.7)' : '#6B7280', textTransform: 'uppercase', margin: 0, marginBottom: 6, letterSpacing: 0.5 }}>{label}</p>
    <p style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>{value}</p>
  </div>;
}

const card = { background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '18px 20px' };
const cardTitle = { fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 14px 0' };
const empty = { color: '#9CA3AF', fontSize: 13, textAlign: 'center', padding: '40px 0', margin: 0 };
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = { padding: '8px 10px', fontWeight: 500, fontSize: 11, color: '#6B7280', textTransform: 'uppercase', borderBottom: '1px solid #E5E7EB', textAlign: 'left' };
const td = { padding: '8px 10px', color: '#111827' };
